import { colors, colorSystem } from '@/styles/theme'
import { ChangeEventHandler, ForwardedRef, forwardRef, InputHTMLAttributes, useId } from 'react'
import styled, { css, RuleSet } from 'styled-components'

type OmitSizeInputHTMLAttributes = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>

interface SwitchProps extends OmitSizeInputHTMLAttributes {
  size?: 'sm' | 'md'
  checked?: boolean
  disabled?: boolean
  onCheckedChange?: (checked: boolean) => void
}

function SwitchComponent(
  { size = 'md', checked, disabled, onCheckedChange, onChange, ...htmlInputProps }: SwitchProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const id = useId()

  // onChange binding
  const _onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(e)
    onCheckedChange?.(e.target.checked)
  }

  return (
    <div>
      <HideInput ref={ref} id={id} type="checkbox" checked={checked} disabled={disabled} onChange={_onChange} {...htmlInputProps} />
      <SwitchWrap size={size} htmlFor={id} />
    </div>
  )
}

const HideInput = styled.input`
  appearance: none;
  position: absolute;
`

const SwitchWrap = styled.label<{ size: NonNullable<SwitchProps['size']> }>`
  border-radius: 16px;
  background-color: ${colors.gray400};
  cursor: pointer;

  transition: all 0.2s ease-in-out;

  display: block;
  position: relative;

  input:checked + & {
    background-color: ${colorSystem.text.common.success};
  }

  input:disabled + & {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &::before {
    content: '';
    background-color: #ffffff;
    border-radius: 99%;

    transition: all 0.2s ease-in-out;

    position: absolute;
    left: 2px;
  }

  ${({ size }) => sizeCSS[size]}
`

const sizeCSS: Record<NonNullable<SwitchProps['size']>, RuleSet> = {
  sm: css({
    width: 28,
    height: 16,
    padding: 2,

    '&::before': {
      width: 12,
      height: 12,
    },
    'input:checked + &::before': {
      left: 'calc(100% - 14px)',
    },
  }),
  md: css({
    width: 42,
    height: 24,
    padding: 3,

    '&::before': {
      width: 18,
      height: 18,
    },
    'input:checked + &::before': {
      left: 'calc(100% - 20px)',
    },
  }),
}

export const Switch = forwardRef(SwitchComponent)
